import {
  MENU_SCAN_MAX_FILES,
  MENU_SCAN_MAX_PDF_BYTES,
  MENU_SCAN_MAX_PDFS,
} from '@pulsify/shared/dto'

export type PickRejection = 'unsupported' | 'too_many' | 'second_pdf' | 'too_large'

/**
 * The file picker's accept list. HEIC is left out on purpose: iOS then hands over its photos as
 * JPEG, which every browser can shrink.
 */
export const MENU_FILE_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf,.pdf'

const IMAGE_NAME = /\.(jpe?g|png|webp|heic|heif)$/i

export function isPdfFile(file: Pick<File, 'name' | 'type'>): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
}

function isImageFile(file: Pick<File, 'name' | 'type'>): boolean {
  return file.type.startsWith('image/') || IMAGE_NAME.test(file.name)
}

/** Tells picked files apart, so picking the same file twice adds it once. */
export function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`
}

/**
 * Which of `incoming` may join `current` for one reading: photos and PDFs only, at most six
 * files, one of them a PDF of at most 15 MB. Photos are shrunk before sending, so their size
 * is checked after that (see `prepareUpload`).
 */
export function pickMenuFiles(
  current: readonly File[],
  incoming: readonly File[],
): { accepted: File[]; rejected: { name: string; reason: PickRejection }[] } {
  const keys = new Set(current.map(fileKey))
  let count = current.length
  let pdfs = current.filter(isPdfFile).length
  const accepted: File[] = []
  const rejected: { name: string; reason: PickRejection }[] = []
  for (const file of incoming) {
    if (keys.has(fileKey(file))) continue
    const pdf = isPdfFile(file)
    let reason: PickRejection | null = null
    if (!pdf && !isImageFile(file)) reason = 'unsupported'
    else if (count >= MENU_SCAN_MAX_FILES) reason = 'too_many'
    else if (pdf && pdfs >= MENU_SCAN_MAX_PDFS) reason = 'second_pdf'
    else if (pdf && file.size > MENU_SCAN_MAX_PDF_BYTES) reason = 'too_large'
    if (reason) {
      rejected.push({ name: file.name, reason })
      continue
    }
    accepted.push(file)
    keys.add(fileKey(file))
    count += 1
    if (pdf) pdfs += 1
  }
  return { accepted, rejected }
}
